import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff, Lock } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomDialogTitle from "../CustomDialogTitle";
import { appColors } from "../../../constants/theme";

const PasswordChangeDialog = ({ open, onClose, onSubmit, loading = false, isRequired = false }) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validationSchema = Yup.object({
    password: Yup.string()
      .min(8, t("password_min_length"))
      .required(t("password_required")),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password")], t("passwords_do_not_match"))
      .required(t("confirm_password_required")),
  });

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (onSubmit) {
        await onSubmit(values.password, values.confirmPassword);
      }
    },
  });

  const handleClose = () => {
    // Eğer zorunlu ise (CreateFirstLogin), dialog kapatılamaz
    if (isRequired) {
      return;
    }
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={isRequired ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
        },
      }}
    >
      <CustomDialogTitle
        title={t("change_password")}
        type="warning"
        onClose={isRequired ? undefined : handleClose}
      />
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
            <Typography variant="body2" sx={{ color: appColors.gray500, mb: 1 }}>
              {t("first_login_password_change_message") || 
                "İlk girişinizde şifrenizi değiştirmeniz gerekmektedir. Lütfen yeni bir şifre belirleyin."}
            </Typography>

            <TextField
              fullWidth
              name="password"
              label={t("new_password")}
              type={showPassword ? "text" : "password"}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: appColors.gray500 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              name="confirmPassword"
              label={t("confirm_password")}
              type={showConfirmPassword ? "text" : "password"}
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
              helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: appColors.gray500 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          {!isRequired && (
            <Button
              onClick={handleClose}
              disabled={loading}
              sx={{ textTransform: "none" }}
            >
              {t("cancel")}
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !formik.isValid}
            sx={{ textTransform: "none" }}
          >
            {loading ? t("saving") : t("save")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PasswordChangeDialog;


